import _ from 'lodash';
import { useCallback, useState } from 'react';

export function updateAtPath<T extends object>(
  obj: T,
  path: string,
  updater: (value: any) => any,
): T {
  const cloned = _.cloneDeep(obj);
  const currentValue = _.get(cloned, path);
  const newValue = updater(currentValue);
  _.set(cloned, path, newValue);
  return cloned;
}

export function updateArrayItem<T>(
  array: T[],
  predicate: (item: T) => boolean,
  updater: (item: T) => T,
): T[] {
  const cloned = _.cloneDeep(array);
  const index = _.findIndex(cloned, predicate);
  if (index === -1) return cloned;

  cloned[index] = updater(cloned[index]);
  return cloned;
}

export function upsertArrayItem<T>(array: T[], predicate: (item: T) => boolean, item: T): T[] {
  const cloned = _.cloneDeep(array);
  const index = _.findIndex(cloned, predicate);

  if (index === -1) {
    cloned.push(item);
  } else {
    cloned[index] = item;
  }

  return cloned;
}

export function removeArrayItem<T>(array: T[], predicate: (item: T) => boolean): T[] {
  return _.reject(array, predicate);
}

export function mergeDeep<T extends object>(target: T, ...sources: Partial<T>[]): T {
  return _.merge(_.cloneDeep(target), ...sources);
}

export function useNestedState<T extends object>(initialState?: T) {
  const [state, setState] = useState<T | undefined>(initialState);

  const updateState = useCallback((updater: (current: T | undefined) => T | undefined) => {
    setState((current) => updater(current));
  }, []);

  const updateAtStatePath = useCallback((path: string, updater: (value: any) => any) => {
    setState((current) => (current ? updateAtPath(current, path, updater) : current));
  }, []);

  const setAtStatePath = useCallback((path: string, value: any) => {
    setState((current) => {
      if (!current) return current;
      const cloned = _.cloneDeep(current);
      _.set(cloned, path, value);
      return cloned;
    });
  }, []);

  const mergeAtStatePath = useCallback((path: string, value: any) => {
    setState((current) => {
      if (!current) return current;
      const cloned = _.cloneDeep(current);
      const existing = _.get(cloned, path);
      _.set(cloned, path, _.merge(existing, value));
      return cloned;
    });
  }, []);

  return {
    state,
    setState,
    updateState,
    updateAtPath: updateAtStatePath,
    setAtPath: setAtStatePath,
    mergeAtPath: mergeAtStatePath,
  };
}

export const nestedUpdatePatterns = {
  updateById: <T extends { id: string }>(array: T[], id: string, updater: (item: T) => T) =>
    updateArrayItem(array, (item) => item.id === id, updater),

  upsertById: <T extends { id: string }>(array: T[], item: T) =>
    upsertArrayItem(array, (existing) => existing.id === item.id, item),

  removeById: <T extends { id: string }>(array: T[], id: string) =>
    removeArrayItem(array, (item) => item.id === id),
};

export const useCellStates = <T>() => {
  const [cellStates, setCellStates] = useState<Map<string, T>>(new Map());

  const updateCellState = useCallback((cellId: string, state: T, clearAfter?: number) => {
    setCellStates((prev) => new Map(prev.set(cellId, state)));
    if (clearAfter) {
      setTimeout(() => {
        setCellStates((prev) => {
          const next = new Map(prev);
          next.delete(cellId);
          return next;
        });
      }, clearAfter);
    }
  }, []);

  const clearCellState = useCallback((cellId: string) => {
    setCellStates((prev) => {
      const next = new Map(prev);
      next.delete(cellId);
      return next;
    });
  }, []);

  const getCellState = useCallback((cellId: string) => cellStates.get(cellId), [cellStates]);

  return { getCellState, updateCellState, clearCellState };
};
